import { useEffect, useRef, useState } from 'react';
import { Eye, Loader2, RotateCw } from 'lucide-react';
import useGoogleFonts from '@/components/settings/look/useGoogleFonts';
import { withHeads } from '@/components/estimating/agreements/preview/scopeHeads';
import useProposal from './useProposal';
import { ProposalHeader, ProposalIntro, hf } from './ProposalHeader';
import ProposalService from './ProposalService';
import { PhoneBar, ProposalTotals } from './ProposalTotals';
import SubmitBox from './SubmitBox';
import ProposalTerms from './ProposalTerms';

function Dead({ retry, message }) {
  return (
    <div className="rounded-md border border-[#D5D8DD] bg-white p-8 text-center" data-testid="proposal-public-dead">
      <h1 className="font-exo text-xl font-bold text-[#0D1B2A]">This proposal link isn’t available</h1>
      <p className="mt-2 text-sm text-[#5C6370]">{message || 'It may have expired or been replaced, the company may be updating your proposal, or it was already sent for signing. Please contact the company that sent it.'}</p>
      {retry && <button type="button" onClick={retry} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#4575A8]" data-testid="proposal-public-retry"><RotateCw className="h-4 w-4" />Try again</button>}
    </div>
  );
}

const PreviewBanner = ({ v }) => (
  <div className="mb-4 flex items-start gap-2 rounded-md border border-[#4575A8]/40 bg-[#E1ECF6] px-4 py-3 text-xs text-[#2F5784]" data-testid="proposal-preview-banner">
    <Eye className="mt-0.5 h-4 w-4 shrink-0" />
    <span><strong>Preview.</strong> This is the page {v.client || 'the client'} gets from the proposal email. Ticking here only updates the total: nothing is saved or sent.</span>
  </div>
);

function Body({ p, preview }) {
  const v = p.st.v;
  useGoogleFonts(v.company.fonts?.google);
  const [name, setName] = useState(v.submitted?.name || v.client || '');
  const [needName, setNeedName] = useState(false);
  const nameRef = useRef(null);
  const color = v.company.primary || '#0D1B2A';
  const label = v.submitted ? 'Update my picks' : 'Send my picks';
  const go = () => {
    if (preview) return;
    if (!name.trim()) {
      setNeedName(true);
      nameRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      nameRef.current?.focus({ preventScroll: true });
      return;
    }
    setNeedName(false);
    p.submit(name.trim());
  };
  const isNew = new Set(v.new);
  const pick = v.can_pick !== false;  // view only: the services and terms, nothing to tick or send
  return (
    <>
      {preview && <PreviewBanner v={v} />}
      <div className="rounded-md border border-[#D5D8DD] bg-white p-5 shadow-sm sm:p-8" style={{ fontFamily: v.company.fonts?.body }} data-testid="proposal-public-card">
        <ProposalHeader v={v} />
        <ProposalIntro v={v} />
        <div className="space-y-3" data-testid="proposal-public-services">
          {withHeads(v, p.priced.services).map(({ head, item: s }, i) => (
            <div key={s.key} className="space-y-3">
              {head != null && <p className="pt-3 text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: v.company.accent }} data-testid={`proposal-scopehead-${i}`}>{head}</p>}
              <ProposalService s={s} i={i} on={!s.optional || p.chosen.includes(s.key)} isNew={isNew.has(s.key)} itemized={v.detail === 'itemized'}
                onToggle={pick ? () => p.toggle(s.key) : null} color={color} hfont={hf(v)} />
            </div>
          ))}
        </div>
        <ProposalTotals totals={p.priced.totals} quoting={p.quoting} color={color} />
        <ProposalTerms terms={p.priced.terms} quoting={p.quoting} color={color} hfont={hf(v)} />
        {pick && (
          <SubmitBox ref={nameRef} v={v} p={p} name={name} setName={(x) => { setName(x); if (x.trim()) setNeedName(false); }} needName={needName}
            label={label} preview={preview} onSubmit={go} color={color} />
        )}
        {v.company.footer && <p className="mt-6 whitespace-pre-line border-t border-[#E6E8EC] pt-3 text-xs text-[#5C6370]">{v.company.footer}</p>}
      </div>
      <PhoneBar totals={p.priced.totals} quoting={p.quoting} color={color} label={pick ? label : null} busy={p.sending} disabled={preview} onSubmit={go} />
    </>
  );
}

// The client's proposal page (Agreements part 1): Required services locked, Optional ones ticked on or off, a live
// total, and "Send my picks". `api` = { load, quote, submit }; `preview` (the estimator's look before sending) saves nothing.
export default function ProposalView({ api, preview = false }) {
  const p = useProposal(api);
  const v = p.st.v;
  useEffect(() => { if (v) document.title = `Proposal${v.project ? ` · ${v.project}` : ''} · ${v.company.name}`; }, [v]);
  return (
    <div className="min-h-screen bg-[#EEF0F3] px-3 pb-32 pt-6 sm:px-4 sm:py-14" data-testid={preview ? 'proposal-preview-page' : 'proposal-public-page'}>
      <div className="mx-auto max-w-2xl">
        {p.st.phase === 'loading' && <div className="flex items-center justify-center gap-2 py-20 text-sm text-[#5C6370]" data-testid="proposal-public-loading"><Loader2 className="h-4 w-4 animate-spin" />Loading your proposal…</div>}
        {p.st.phase === 'dead' && <Dead retry={p.st.retry ? p.load : null} message={p.st.message} />}
        {p.st.phase === 'ok' && <Body p={p} preview={preview} />}
      </div>
    </div>
  );
}
